import {useCallback} from 'react';
import {Modality} from '@domain/model/Account/Subscription/Subscription';
import {AccountApplication} from '@application/Account';
import {TEXT} from '@views/Account/Plan/MyPlan/Actions/ActionChangePlan/PanelChangePlan/PanelChangePlan.definition';
import {useCurrentPlan} from '@views/Account/Plan/_hooks/useCurrentPlan';
import {isCancellingPlan} from '../_utils/isCancellingPlan';
import {
  emitEventSubscriptionCancelled,
  emitEventSubscriptionUpdated,
} from '@views/Account/Plan/MyPlan/MyPlan.controller';
import {useToast} from '@components/_context';
import {useIntl} from 'react-intl';

const emitSubscriptionEvent = (currentModality: Modality, newModality: Modality): Promise<void> => {
  if (isCancellingPlan(newModality)) {
    return emitEventSubscriptionCancelled(currentModality);
  } else {
    return emitEventSubscriptionUpdated(currentModality, newModality);
  }
};

interface UseUpdatePlanResult {
  updatePlan: (modality: Modality, onSuccess: () => Promise<void>) => Promise<void>;
}

export const useUpdatePlan = (): UseUpdatePlanResult => {
  const {day, modality: currentModality} = useCurrentPlan();
  const {showError, showSuccess} = useToast();
  const {formatMessage} = useIntl();

  const updatePlan: UseUpdatePlanResult['updatePlan'] = useCallback(
    async (modality, onSuccess) => {
      try {
        const lkDay = !day ? {lkDay: 1} : {};
        await AccountApplication.setSubscription({modality, ...lkDay});
        const successMessage = isCancellingPlan(modality) ? TEXT.MSG_SUCCESS_PLAN_CANCEL : TEXT.MSG_SUCCESS_PLAN_UPDATE;
        await emitSubscriptionEvent(currentModality, modality);
        await onSuccess();
        showSuccess({message: formatMessage(successMessage)});
      } catch (error) {
        showError({message: formatMessage(TEXT.MSG_ERROR)});
      }
    },
    [currentModality, day, formatMessage, showError, showSuccess],
  );

  return {updatePlan};
};
